import * as THREE from "three";
import { CSS3DObject } from "three/examples/jsm/renderers/CSS3DRenderer";

import Experience from "../Experience";
import { toThreeVector } from "../Utils/aeCoords";
import { evalLayerState } from "../Utils/layerAnim";

// Figma content frame's own pixel size (node 2290:1549 "Frame 1") — the DOM
// is built at this literal CSS size, then CSS3DObject.scale converts CSS
// pixels to world units (see setTransform) so the card lands at the right
// place/size in the 3D scene regardless of viewport size.
const CARD_WIDTH = 592;
const CARD_HEIGHT = 630;

const _tl = new THREE.Vector3();
const _tr = new THREE.Vector3();
const _bl = new THREE.Vector3();
const _ex = new THREE.Vector3();
const _down = new THREE.Vector3();
const _exN = new THREE.Vector3();
const _eyN = new THREE.Vector3();
const _ez = new THREE.Vector3();
const _centre = new THREE.Vector3();

// Replaces the old "Video Text" WebGL card (a static Text.png backdrop)
// with a real HTML/CSS composition — matches the Figma design exactly
// (heading, video preview with a play button + label, paragraph, divider,
// caption) rather than a single flat image, and renders via CSS3DRenderer
// so it's real DOM/CSS (crisp text, an actual <video> element) while still
// tracking the same corner-pin position and camera perspective as every
// other layer. Deliberately NOT graded/FXAA'd like the WebGL layers (see
// conversation) — this is a UI card sitting on top of the scene, matching
// how the Figma design itself renders it.
export default class VideoTextDom {
  constructor() {
    this.experience = new Experience();
    this.sceneData = this.experience.sceneData;
    this.css3d = this.experience.css3dRenderer;

    this.layer = this.sceneData.getLayer("Video Text");
    this.config = this.sceneData.preset.layers.find((l) => l.name === "Video Text");
    this.anims = this.config.anims || [];

    // See TextCard for why this is a continuous per-tick ease rather than a
    // one-shot tween (reference: stepAnim).
    this.visibility = 0;

    this.setConfig();
    this.setAnchorFrame();
    this.buildDom();
    this.createObject();
  }

  setConfig() {
    const c = this.config;
    // Raw, not /100 — see TextCard.setConfig for the same convention.
    this.shiftX = Number(c.shiftX);
    this.shiftY = Number(c.shiftY);
    this.shiftZ = Number(c.shiftZ);
    this.scale = Number(c.scalePct) / 100;
    this.fadeDuration = Number(c.dur) || 1;
  }

  setAnchorFrame() {
    const compW = this.sceneData.scene.comp.width;
    const compH = this.sceneData.scene.comp.height;
    const frame = this.layer.frames[0];

    _tl.copy(toThreeVector(frame.tl, compW, compH));
    _tr.copy(toThreeVector(frame.tr, compW, compH));
    _bl.copy(toThreeVector(frame.bl, compW, compH));

    _ex.subVectors(_tr, _tl);
    _down.subVectors(_bl, _tl);
    _ez.crossVectors(_ex, _down.clone().negate()).normalize();
    _exN.copy(_ex).normalize();
    _eyN.copy(_down).normalize().negate();

    this.centre = _tl.clone().addScaledVector(_ex, 0.5).addScaledVector(_down, 0.5);
    this.exN = _exN.clone();
    this.eyN = _eyN.clone();
    this.ez = _ez.clone();
    // World units per CSS pixel of the DOM card's own authored size —
    // NOT layer.width (the layer's pre-scale AE bounds, a separate,
    // unrelated metadata field the reference never re-derives size from;
    // confirmed against applyFrame's non-text branch: ex2 = ex * L.scale,
    // built straight from the baked corner vectors, no layer.width/height
    // involved at all). The corner-pin quad's own world-space width (ex's
    // length) already IS this layer's authored size at scalePct 100 — so
    // the CSS card (592px) should map onto ex.length() directly, then
    // scalePct scales that further, exactly like every other layer.
    this.worldUnitsPerPx = _ex.length() / CARD_WIDTH;
  }

  // Builds the exact Figma layout (node 2290:1549) as real DOM: heading,
  // video preview with play button + label, paragraph, divider, caption.
  //
  // Flex+gap lives on an INNER wrapper, not on the CSS3DObject root —
  // CSS3DRenderer overwrites `element.style.display` to '' every frame
  // (see CSS3DRenderer.js ~377), which resets a div to `display:block`
  // and silently kills flex gap. Measured: children were stacked flush
  // (offsetTops 0, 16, 346, 432, 483) despite inline gap:30px.
  buildDom() {
    const root = document.createElement("div");
    Object.assign(root.style, {
      width: `${CARD_WIDTH}px`,
      height: `${CARD_HEIGHT}px`,
      opacity: "0",
      filter: "blur(0px)",
    });

    const inner = document.createElement("div");
    Object.assign(inner.style, {
      width: "100%",
      height: "100%",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: "30px",
      fontFamily: '"Hyundai Sans Head", sans-serif',
      color: "#ffffff",
    });

    // Figma 2290:1551 — text-[20px] leading-[16px] tracking-[0.3px],
    // content width ~295 (nowrap, centered by the flex column).
    const heading = document.createElement("p");
    heading.textContent = "Six reasons to choose Hyundai";
    Object.assign(heading.style, {
      margin: "0",
      flexShrink: "0",
      fontSize: "20px",
      fontWeight: "500",
      letterSpacing: "0.3px",
      lineHeight: "16px",
      textAlign: "center",
      whiteSpace: "nowrap",
    });

    const videoWrap = document.createElement("div");
    Object.assign(videoWrap.style, {
      position: "relative",
      width: "591px",
      height: "330px",
      borderRadius: "22px",
      overflow: "hidden",
      flexShrink: "0",
      // Re-enables pointer interaction on top of the pass-through root (see
      // Css3dRenderer) — hover over the whole card (not just the play row)
      // triggers the play-button hover effect below.
      pointerEvents: "auto",
    });

    // Shown immediately (a real decoded frame, not a blank/transparent
    // gap) and cross-fades out once the video actually starts playing —
    // see startVideoOnceConnected(). Sits above the <video> in paint order
    // so it's what's visible while readyState is still 0.
    this.thumbnail = document.createElement("img");
    this.thumbnail.src = "/hyundai-tco/video-poster.jpg";
    Object.assign(this.thumbnail.style, {
      position: "absolute",
      inset: "0",
      width: "100%",
      height: "100%",
      objectFit: "cover",
      opacity: "1",
      transition: "opacity 0.5s ease",
    });

    this.video = document.createElement("video");
    Object.assign(this.video.style, {
      position: "absolute",
      inset: "0",
      width: "100%",
      height: "100%",
      objectFit: "cover",
    });
    this.video.poster = "/hyundai-tco/video-poster.jpg";
    // src is NOT set here — CSS3DRenderer only actually appendChild()s this
    // element into the live document lazily, inside its own render() pass
    // (not at scene.add() time), so at construction the <video> is
    // guaranteed detached. Setting .src on a detached <video> doesn't
    // reliably start the network fetch at all in some engines (confirmed:
    // readyState stayed 0/HAVE_NOTHING indefinitely even after the element
    // later became connected) — startVideoOnceConnected() below sets src
    // for the first time only once isConnected is actually true.
    this.video.loop = true;
    this.video.muted = true;
    this.video.defaultMuted = true;
    this.video.playsInline = true;
    this.video.autoplay = true;
    this.video.preload = "auto";

    const playRow = document.createElement("div");
    Object.assign(playRow.style, {
      position: "absolute",
      left: "20.5px",
      top: "265px",
      display: "flex",
      alignItems: "center",
      gap: "15px",
      pointerEvents: "auto",
      cursor: "pointer",
    });

    const playButton = document.createElement("div");
    Object.assign(playButton.style, {
      width: "50px",
      height: "50px",
      borderRadius: "10px",
      background: "rgba(255, 255, 255, 0.15)",
      backdropFilter: "blur(5.5px)",
      WebkitBackdropFilter: "blur(5.5px)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: "0",
      transition: "background 0.2s ease",
    });
    const playIcon = document.createElement("img");
    playIcon.src = "/hyundai-tco/icons/play.svg";
    playIcon.alt = "";
    Object.assign(playIcon.style, {
      width: "20px",
      height: "20px",
      display: "block",
      transition: "filter 0.2s ease",
    });
    playButton.appendChild(playIcon);

    // Hover anywhere over the video card (not just the play row): button
    // background goes solid white, icon flips from white to black —
    // brightness(0) zeroes the SVG's white fill to black regardless of its
    // baked fill-opacity, then back off on mouseleave.
    videoWrap.addEventListener("mouseenter", () => {
      playButton.style.background = "#ffffff";
      playIcon.style.filter = "brightness(0)";
    });
    videoWrap.addEventListener("mouseleave", () => {
      playButton.style.background = "rgba(255, 255, 255, 0.15)";
      playIcon.style.filter = "none";
    });

    // Figma 2290:1558 — gap 3px; title 12/19 Medium @60%, desc 18/24 Medium tracking 0.3.
    const label = document.createElement("div");
    Object.assign(label.style, {
      display: "flex",
      flexDirection: "column",
      gap: "3px",
      flexShrink: "0",
    });
    const labelTop = document.createElement("p");
    labelTop.textContent = "Introduction";
    Object.assign(labelTop.style, {
      margin: "0",
      fontSize: "12px",
      fontWeight: "500",
      lineHeight: "19px",
      opacity: "0.6",
      whiteSpace: "nowrap",
    });
    const labelBottom = document.createElement("p");
    labelBottom.textContent = "How it started";
    Object.assign(labelBottom.style, {
      margin: "0",
      fontSize: "18px",
      fontWeight: "500",
      letterSpacing: "0.3px",
      lineHeight: "24px",
      whiteSpace: "nowrap",
    });
    label.append(labelTop, labelBottom);

    playRow.append(playButton, label);
    // thumbnail last — paints on top of the video until it fades out.
    videoWrap.append(this.video, this.thumbnail, playRow);

    this.playing = true;
    playRow.addEventListener("click", () => {
      if (this.playing) this.video.pause();
      else this.video.play().catch(() => {});
      this.playing = !this.playing;
    });

    // Figma 2290:1562 — text-[24px] leading-[1.2] tracking-[-0.72px],
    // width 592 (= full card). Wraps to 3 lines / ~87px tall.
    const paragraph = document.createElement("p");
    paragraph.textContent =
      "Every episode carries a reason to choose Hyundai: genuine parts, real quality, fuel efficiency, trusted service, and lasting value.";
    Object.assign(paragraph.style, {
      margin: "0",
      width: "100%",
      maxWidth: `${CARD_WIDTH}px`,
      flexShrink: "0",
      fontSize: "24px",
      fontWeight: "500",
      textAlign: "center",
      letterSpacing: "-0.72px",
      lineHeight: "1.2",
      textShadow: "1px 1px 1px rgba(0, 0, 0, 0.3)",
    });

    // Figma Line 1 — 51px vertical stroke, centered above the caption.
    const dividerWrap = document.createElement("div");
    Object.assign(dividerWrap.style, {
      width: "100%",
      height: "51px",
      flexShrink: "0",
      display: "flex",
      justifyContent: "center",
    });
    const divider = document.createElement("div");
    Object.assign(divider.style, {
      width: "1px",
      height: "51px",
      background: "#ffffff",
    });
    dividerWrap.appendChild(divider);

    // Figma 2290:1564 — text-[16px] Regular leading-[1.6], nowrap (~254px).
    const caption = document.createElement("p");
    caption.textContent = "Follow the mini series to find yours.";
    Object.assign(caption.style, {
      margin: "0",
      flexShrink: "0",
      fontSize: "16px",
      fontWeight: "400",
      textAlign: "center",
      lineHeight: "1.6",
      whiteSpace: "nowrap",
      textShadow: "1px 1px 1px rgba(0, 0, 0, 0.3)",
    });

    inner.append(heading, videoWrap, paragraph, dividerWrap, caption);
    root.appendChild(inner);

    this.root = root;
    this.inner = inner;
    this.playIcon = playIcon;
  }

  createObject() {
    this.object = new CSS3DObject(this.root);
    this.css3d.scene.add(this.object);
    this.updateTransform();
  }

  updateTransform() {
    const s = this.worldUnitsPerPx * this.scale;
    this.object.scale.set(s, s, s);

    _centre
      .copy(this.centre)
      .addScaledVector(this.exN, this.shiftX)
      .addScaledVector(this.eyN, this.shiftY)
      .addScaledVector(this.ez, this.shiftZ);
    this.object.position.copy(_centre);

    // Orient the DOM plane to the layer's own basis (ex/ey/ez) — same
    // construction as the WebGL cards' makeBasis calls. No manual axis
    // flip needed: CSS3DRenderer's own getObjectCSSMatrix already negates
    // the object's local Y column internally to reconcile CSS's
    // screen-space (Y-down) convention with Three's (Y-up), so this basis
    // is built the exact same way as every WebGL layer's.
    const m = new THREE.Matrix4().makeBasis(this.exN, this.eyN, this.ez);
    this.object.quaternion.setFromRotationMatrix(m);
  }

  // Sets src (starting the actual network fetch/decode) the first time the
  // card's section is actually reached — see the comment in buildDom() for
  // why this can't happen at construction time (the element is guaranteed
  // detached until CSS3DRenderer's own render() pass runs). The
  // poster/thumbnail image covers the video the whole time it's loading
  // (real content, not a transparent gap), then cross-fades out once a
  // frame is actually playing — "playing" rather than "loadeddata" so the
  // fade starts only once motion is genuinely on screen, not a moment
  // before it.
  startVideo() {
    if (this.videoStarted || !this.video.isConnected) return;
    this.videoStarted = true;

    this.video.src = "/hyundai-tco/video-loop.mp4";
    this.video.addEventListener("playing", () => {
      this.thumbnail.style.opacity = "0";
    });
    this.video.addEventListener("canplay", () => {
      this.video.play().catch(() => {});
    });
    // Assigning .src is supposed to invoke the media load algorithm on its
    // own per spec, but that didn't happen reliably here even once
    // connected (readyState stayed 0 indefinitely) — explicitly calling
    // load() is the documented, reliable way to force it.
    this.video.load();
  }

  update(dt, playheadFrame) {
    const target = evalLayerState(this.anims, this.sceneData.clips, playheadFrame);

    // Gate playback on the section's own show/hide target (not the eased
    // this.visibility, which still ramps from 0 the instant the section is
    // reached, and not element-connectedness alone) — the video must stay
    // paused and silent until the playhead actually scrolls into "Video
    // Text"'s clip range, matching every other card's reveal timing.
    if (target > 0) {
      this.startVideo();
      // Resumes after a hide->show round trip — canplay (which drives the
      // initial play() in startVideo) only fires once per load, so coming
      // back to this section a second time needs an explicit nudge.
      if (this.videoStarted && this.video.paused && this.video.readyState >= 2) {
        this.video.play().catch(() => {});
      }
    } else if (this.videoStarted && !this.video.paused) {
      this.video.pause();
    }

    if (this.visibility !== target) {
      const step = dt / Math.max(0.02, this.fadeDuration);
      this.visibility =
        target > this.visibility
          ? Math.min(target, this.visibility + step)
          : Math.max(target, this.visibility - step);
    }

    // Same blur+opacity fade as the WebGL cards (cardFade.js), done in CSS
    // instead of a fragment shader since this is a real DOM element.
    // Visibility uses object.visible — do NOT toggle root.style.display;
    // CSS3DRenderer owns that property and resets it to '' (=block) every
    // frame, which is what broke flex gap when display:flex lived on root.
    this.root.style.opacity = String(this.visibility);
    this.root.style.filter = `blur(${5 * (1 - this.visibility)}px)`;
    this.object.visible = this.visibility > 0.001;
  }
}
